<template>
  <div class="row">
    <div class="col-12">
      <h2 class="h--headingM">Précisez le montant de votre dépense ? <span class="asterix">*</span></h2>
      <!-- Info Bloc -->
      <div class="infoBloc mt-4 mb-4" v-if="(tarificationStore === 'CentPourCent' || tarificationStore === 'TarifMaitrise') && maxMontantDepense">
        <div class="infoBloc__icon">
          <inline-svg src="/images/icones/icon_information.svg" />
        </div>
        <p class="infoBloc__text">
          Le montant maximal dépensé ne peut pas dépasser {{ maxMontantDepense }}&nbsp;€ dans le cadre du
          {{ tarificationStore === "CentPourCent" ? "dispositif 100% Santé" : "tarif maîtrisé" }}.
        </p>
      </div>
      <div
        class="infoBloc mt-4 mb-4"
        v-if="
          this.prestationStore.key === 'MontureVerresSimplesHorsCentPourCentSante' || this.prestationStore.key === 'MontureVerresComplexesHorsCentPourCentSante'
        "
      >
        <div class="infoBloc__icon">
          <inline-svg src="/images/icones/icon_information.svg" />
        </div>
        <p class="infoBloc__text">
          Votre monture est prise en charge à 100&nbsp;% si elle ne dépasse pas un montant de {{ this.sousPrestationStore[0]?.montantMaximal }}&nbsp;€ pour le
          régime de base{{
            this.sousPrestationStore[1]?.montantMaximal !== null
              ? " et un montant de " + this.sousPrestationStore[1]?.montantMaximal + "&nbsp;€ pour le régime avec l'option 1"
              : ""
          }}. Pour les verres le montant remboursés dépend de votre correction et du niveau de garantie de votre contrat.
        </p>
      </div>

      <template v-if="!isPrestationComposite">
        <div class="row mb-3 montantDepense__row">
          <div class="col-12 col-md-6">
            <div class="input-group">
              <input
                type="number"
                class="form-control"
                :class="{ 'is-invalid': v$.montantDispenseStore.$error }"
                v-model.number="montantDispenseStore"
                min="0"
                :max="maxMontantDepense || undefined"
                step="0.01"
                :placeholder="montantDispenseStore || '0.00'"
                @focus="onMontantFocus"
                @input="onMontantInput"
                @blur="onMontantBlur()"
              />
              <span class="input-group-text"><inline-svg src="/images/icones/icon_euro.svg" /></span>
            </div>
            <div v-if="v$.montantDispenseStore.$error" class="invalid-feedback d-block">
              <span v-for="error in v$.montantDispenseStore.$errors" :key="error.$uid">
                {{ error.$message }}
              </span>
            </div>
            <div v-if="displaySubInfo" class="input-group-subinfo">Montant d'un semestre de soin</div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="row align-items-end montantDepense__row">
          <div class="col-12 col-md-6 position-relative" v-for="(sousPrestation, index) in sousPrestationStore" :key="index">
            <label class="input-label">
              {{ sousPrestation.label }}
            </label>
            <div class="input-group">
              <input
                type="number"
                class="form-control"
                :class="{ 'is-invalid': v$.sousPrestationStore.$each.$response.$errors[index]?.montant?.length > 0 }"
                v-model.number="sousPrestation.montant"
                min="0"
                :max="sousPrestation.montantMaximal || undefined"
                step="0.01"
                :placeholder="sousPrestation.placeholder || '0.00'"
                @focus="onMontantFocus"
                @blur="onMontantBlur(index)"
                @input="onMontantInput($event, index)"
              />
              <span class="input-group-text"><inline-svg src="/images/icones/icon_euro.svg" /></span>
            </div>
            <div v-if="v$.sousPrestationStore.$each.$response.$errors[index]?.montant?.length > 0" class="invalid-feedback d-block">
              <span v-for="error in v$.sousPrestationStore.$each.$response.$errors[index].montant" :key="error.$uid">
                {{ error.$message }}
              </span>
            </div>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { watch, computed } from "vue";
import useVuelidate from "@vuelidate/core";
import { helpers } from "@vuelidate/validators";
import simulateurService from "@/services/simulateurService";
import { buildLabel, computeMontantTotal, mapTypePrestation, mapTypeSoin, mapTypeTarification, pushSimulationEvent } from "@/helpers/matomo";
import { normalizeRouteContext } from "@/helpers/entrySource";

export default {
  name: "EtapeMontantDispense",
  components: {},
  emits: ["update:valid"],
  props: {
    selectedTypeRemboursementValue: Number,
  },
  setup(props, { emit }) {
    const v$ = useVuelidate();

    const isValid = computed(() => {
      // Validation simple
      const montantOk = !v$.value.montantDispenseStore?.$invalid;

      // Validation du tableau ($each)
      const sousPrestationsErrors = v$.value.sousPrestationStore?.$each?.$response?.$errors || [];

      const sousPrestationsOk = sousPrestationsErrors.every((item) => !item?.montant || item.montant.length === 0);

      return true; //montantOk && sousPrestationsOk //SI VALIDATION A REMETTRE
    });

    watch(
      isValid,
      (val) => {
        emit("update:valid", val);
      },
      { immediate: true },
    );

    return { v$, isValid };
  },
  data() {
    const routeContext = normalizeRouteContext(this.$route.params);

    return {
      // Données de l'URL
      clientCode: this.$route.params.clientCode,
      sourceEntree: routeContext.sourceEntree,
      typeRegime: routeContext.typeRegime,

      configurationPrestation: null, // Configuration de la prestation (avec montants max)
      modificationMontantTimeout: null,
    };
  },
  beforeUnmount() {
    if (this.modificationMontantTimeout) {
      clearTimeout(this.modificationMontantTimeout);
    }
  },
  validations() {
    return {
      montantDispenseStore: {
        maxMontantAllowed: helpers.withMessage(
          () => {
            if (this.maxMontantDepense !== null) {
              return `Le montant ne peut pas dépasser ${this.maxMontantDepense}€ pour cette prestation.`;
            }
            return "";
          },
          (value) => {
            if (this.maxMontantDepense === null) return true;
            return true; //value <= this.maxMontantDepense //SI VALIDATION A REMETTRE
          },
        ),
      },
      // Validation pour les sous-prestations
      sousPrestationStore: {
        $each: helpers.forEach({
          montant: {
            maxMontantAllowed: helpers.withMessage(
              ({ $model, $params }) => {
                const sousPrestation = this.sousPrestationStore.find((sp) => sp.montant === $model);
                if (sousPrestation?.montantMaximal !== null) {
                  return `Le montant pour ${sousPrestation.label} ne peut pas dépasser ${sousPrestation.montantMaximal}€.`;
                }
                return "";
              },
              (value, sousPrestation) => {
                const sp = this.sousPrestationStore.find((s) => s.montant === value);
                if (sp?.montantMaximal == null) return true;
                return true; //value <= sp.montantMaximal //SI VALIDATION A REMETTRE
              },
            ),
          },
        }),
      },
    };
  },
  async mounted() {
    console.debug("Component Montant Depense is mounted!");

    // Charger les données initiales
    await this.onTypePrestationChange();
  },
  computed: {
    typeDepenseStore() {
      return this.$store.getters["dataSimulateurStore/typeDepense"];
    },
    prestationStore() {
      return this.$store.getters["dataSimulateurStore/prestation"];
    },
    prestationsAllStore() {
      return this.$store.getters["dataSimulateurStore/prestationsAll"];
    },
    tarificationStore() {
      return this.$store.getters["dataSimulateurStore/tarification"];
    },
    montantDispenseStore: {
      get() {
        return this.$store.getters["dataSimulateurStore/montantDepense"];
      },
      set(value) {
        this.$store.dispatch("dataSimulateurStore/updateMontantDepense", Number(value));
      },
    },
    sousPrestationStore: {
      get() {
        return this.$store.getters["dataSimulateurStore/sousPrestations"];
      },
      set(value) {
        this.$store.dispatch("dataSimulateurStore/updateSousPrestations", value);
      },
    },

    maxMontantDepense() {
      // Utiliser le montant maximal de la configuration si disponible
      if (this.configurationPrestation?.montantMaximal != null) {
        return this.configurationPrestation.montantMaximal;
      }
      return null; // Pas de limite
    },
    isPrestationComposite() {
      // Déterminé par la présence de sous-prestations chargées depuis l'API
      return this.sousPrestationStore.length > 0;
    },
    valuePrestation() {
      if (
        this.prestationStore?.key === "TarifNonDefiniTotal" ||
        this.prestationStore?.key === "TarifNonDefiniSemi" ||
        this.prestationStore?.key === "TarifDefini"
      ) {
        switch (this.tarificationStore) {
          case "CentPourCent":
            return this.prestationsAllStore.find((item) => item.key === "CouronneCeramoMetalliqueCentPourCentSante").value;
          case "TarifMaitrise":
            return this.prestationsAllStore.find((item) => item.key === "CouronneCeramoMetalliqueTarifMaitrise").value;
          case "TarifDefini":
            return this.prestationsAllStore.find((item) => item.key === "CouronneCeramoMetalliqueTarifLibre").value;
        }
      } else {
        return this.prestationStore?.value;
      }
    },
    displaySubInfo() {
      return (
        this.prestationStore?.key === "OrthodontieNonRembourseeParSecuriteSociale" || this.prestationStore?.key === "OrthodontieRembourseeParSecuriteSociale"
      );
    },
  },

  methods: {
    getMontantTrackingContext() {
      const typeSoin = mapTypeSoin(this.typeDepenseStore);
      const typePrestation = mapTypePrestation(this.prestationStore);
      const typeTarification = mapTypeTarification(this.tarificationStore);
      const montantTotal = computeMontantTotal(this.montantDispenseStore, this.sousPrestationStore);

      return {
        montantTotal,
        label: buildLabel({
          typeSoin,
          typePrestation,
          typeTarification,
        }),
      };
    },

    onMontantFocus() {
      const context = this.getMontantTrackingContext();

      pushSimulationEvent({
        event: "focus_montant",
        action: "focus_montant",
        label: context.label,
      });
    },

    onMontantBlur(index = null) {
      if (index === null || index === undefined) {
        this.v$.montantDispenseStore.$touch();
      } else {
        this.v$.sousPrestationStore.$touch();
      }

      const context = this.getMontantTrackingContext();

      pushSimulationEvent({
        event: "saisie_montant",
        action: "saisie_montant",
        label: context.label,
        payload: {
          montant_total: context.montantTotal,
        },
        value: context.montantTotal,
      });
    },

    onMontantInput(event, index = null) {
      if (index !== null && index !== undefined) {
        this.updateMontantSousPrestation(event, index);
      }

      if (this.modificationMontantTimeout) {
        clearTimeout(this.modificationMontantTimeout);
      }

      this.modificationMontantTimeout = setTimeout(() => {
        const context = this.getMontantTrackingContext();

        pushSimulationEvent({
          event: "modification_montant",
          action: "modification_montant",
          label: context.label,
          payload: {
            montant_total: context.montantTotal,
          },
          value: context.montantTotal,
        });
      }, 400);
    },

    async onTypePrestationChange() {
      this.resetMontants();

      // Charger la configuration de la prestation sélectionnée
      if (this.prestationStore !== null) {
        const config = await simulateurService.getConfigurationPrestation(
          this.clientCode,
          this.typeRegime,
          this.typeDepenseStore,
          this.valuePrestation,
          this.selectedTypeRemboursementValue,
        );

        // Stocker la configuration pour accéder au montantMaximal
        this.configurationPrestation = config;

        if (config && config.estComposite) {
          // Prestation composite : créer les champs depuis l'API
          this.sousPrestationStore = config.champsMontants.map((champ) => ({
            code: champ.code,
            label: champ.label,
            montant: 0,
            placeholder: champ.placeholder || "0.00",
            montantMaximal: champ.montantMaximal, // Stocker le montant maximal
          }));
        }
      }
    },

    resetMontants() {
      this.montantDispenseStore = 0;
      this.sousPrestationStore = [];
      this.configurationPrestation = null;
      this.v$.$reset(); // Réinitialiser les validations
    },

    updateMontantSousPrestation(event, index) {
      this.$store.dispatch("dataSimulateurStore/updateMontantSousPrestations", {
        index,
        value: Number(event.target.value),
      });
    },
  },
  watch: {
    async typeDepenseStore(newValue, oldValue) {
      await this.onTypePrestationChange();
    },
    async prestationStore(newValue, oldValue) {
      await this.onTypePrestationChange();
    },
  },
};
</script>
